import React from 'react';
import Icon from '../../../components/AppIcon';

const ArticleCard = ({ article, onClick }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTopic = (topic) => {
    return topic?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  const calculateReadingTime = (content) => {
    if (!content) return '5 min read';
    const wordsPerMinute = 200;
    const wordCount = content?.split(/\s+/)?.length;
    const minutes = Math?.ceil(wordCount / wordsPerMinute);
    return `${minutes} min read`;
  };

  const handleShare = (platform, e) => {
    e?.stopPropagation();
    const url = encodeURIComponent(window?.location?.origin + `/blog-article-view/${article?.slug}`);
    const title = encodeURIComponent(article?.title);
    
    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      default:
        return;
    }
    
    window?.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div
      onClick={onClick}
      className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
    >
      {/* Featured Image */}
      {article?.featuredImageUrl ? (
        <div className="aspect-video overflow-hidden bg-muted relative">
          <img
            src={article?.featuredImageUrl}
            alt={article?.title || 'Article featured image'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {/* Featured Badge */}
          {article?.featured && (
            <div className="absolute top-12 right-12">
              <span className="inline-flex items-center gap-4 px-10 py-6 bg-yellow-500 text-white text-xs font-bold rounded shadow-lg">
                <Icon name="Star" size={14} />
                FEATURED
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-video bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center relative">
          <Icon name="FileText" size={48} className="text-muted-foreground" />
          {/* Featured Badge */}
          {article?.featured && (
            <div className="absolute top-12 right-12">
              <span className="inline-flex items-center gap-4 px-10 py-6 bg-yellow-500 text-white text-xs font-bold rounded shadow-lg">
                <Icon name="Star" size={14} />
                FEATURED
              </span>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="p-20">
        {/* Topic Badge */}
        <div className="mb-12">
          <span className="inline-flex items-center px-8 py-4 bg-primary/10 text-primary text-xs font-medium rounded">
            {formatTopic(article?.topic)}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-lg md:text-xl font-heading font-semibold mb-8 line-clamp-2 group-hover:text-primary transition-colors">
          {article?.title}
        </h3>

        {/* Excerpt */}
        <p className="text-sm text-muted-foreground mb-16 line-clamp-3">
          {article?.excerpt}
        </p>

        {/* Meta Information */}
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-16">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-4">
              <Icon name="User" size={14} />
              <span>{article?.authorName || 'Rovyn Team'}</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-4">
              <Icon name="Calendar" size={14} />
              <span>{formatDate(article?.publishedAt || article?.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Reading Time & Social Share */}
        <div className="flex items-center justify-between pt-16 border-t border-border">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <Icon name="Clock" size={14} />
            <span>{calculateReadingTime(article?.content)}</span>
          </div>
          
          {/* Social Share Buttons */}
          <div className="flex items-center gap-8">
            <button
              onClick={(e) => handleShare('twitter', e)}
              className="p-6 hover:bg-muted rounded transition-colors"
              aria-label="Share on Twitter"
            >
              <Icon name="Twitter" size={16} className="text-muted-foreground hover:text-foreground" />
            </button>
            <button
              onClick={(e) => handleShare('facebook', e)}
              className="p-6 hover:bg-muted rounded transition-colors"
              aria-label="Share on Facebook"
            >
              <Icon name="Facebook" size={16} className="text-muted-foreground hover:text-foreground" />
            </button>
            <button
              onClick={(e) => handleShare('linkedin', e)}
              className="p-6 hover:bg-muted rounded transition-colors"
              aria-label="Share on LinkedIn"
            >
              <Icon name="Linkedin" size={16} className="text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        </div>

        {/* View Count */}
        {article?.viewCount > 0 && (
          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-12">
            <Icon name="Eye" size={14} />
            <span>{article?.viewCount?.toLocaleString()} views</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleCard;